/** SPEC §24.2 en §25: Beheer › Instellingen › Beveiliging — Teams-app, domeinen, SAML, SSO verplicht en rollen uit groepen. */
import { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ROL_LABEL, type Rol } from '@ep/core';
import { api } from '../lib/api';
import { Icoon } from '../ui/Icoon';
import { toast } from '../ui/toast';
import { Badge, Laden, Veld, VerwijderKnop } from '../ui/ui';

interface SsoStand {
  verplicht: boolean;
  groepRollen: { groep: string; rol: Rol }[];
  saml: { idpEntityId: string; ssoUrl: string; metadataUrl: string | null; certificaten: number; naamAttribuut: string | null; groepAttribuut: string | null; getest: string | null } | null;
  sp: { entityId: string; acsUrl: string; metadataUrl: string };
  domeinen: { id: string; domein: string; geverifieerd: string | null; record: { naam: string; waarde: string } }[];
  microsoft: { gekoppeld: boolean; beschikbaar: boolean };
  ikViaBedrijf: boolean;
  teams: { beschikbaar: boolean };
}

const kopieer = async (t: string) => {
  try {
    await navigator.clipboard.writeText(t);
    toast('Gekopieerd', 'succes');
  } catch {
    toast('Kopiëren lukt niet; selecteer de tekst en kopieer zelf', 'fout');
  }
};
const Kopie = ({ label, waarde }: { label: string; waarde: string }) => (
  <div className="rij" style={{ gap: 8, alignItems: 'center' }}>
    <span className="klein mute" style={{ minWidth: 120 }}>{label}</span>
    <code className="groei" style={{ wordBreak: 'break-all' }}>{waarde}</code>
    <button className="icoon-knop" title="Kopiëren" onClick={() => void kopieer(waarde)}><Icoon naam="copy" maat={14} /></button>
  </div>
);
const ROLLEN: Rol[] = ['BEHEERDER', 'PLANNER', 'PROJECTMANAGER', 'ENGINEER'];

export function Beveiliging() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['sso'], queryFn: () => api.get<SsoStand>('/api/sso') });
  const [domein, setDomein] = useState('');
  const [saml, setSaml] = useState<{ metadataUrl: string; metadataXml: string; naamAttribuut: string; groepAttribuut: string } | null>(null);
  const [groepen, setGroepen] = useState<{ groep: string; rol: Rol }[] | null>(null);
  const [bezig, setBezig] = useState(false);

  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    if (p.get('saml') === 'ok') toast('Test-login met SAML gelukt', 'succes');
    if (p.get('saml') === 'fout') toast(`Test-login mislukt: ${p.get('melding') ?? ''}`, 'fout');
  }, []);

  const doe = async (f: () => Promise<unknown>, melding?: string) => {
    setBezig(true);
    try {
      await f();
      await qc.invalidateQueries({ queryKey: ['sso'] });
      if (melding) toast(melding, 'succes');
      return true;
    } catch (e) {
      toast((e as Error).message, 'fout');
      return false;
    } finally {
      setBezig(false);
    }
  };
  if (!q.data) return <Laden />;
  const d = q.data;
  const geverifieerd = d.domeinen.filter((x) => x.geverifieerd).length;
  const bedrijfsMethode = d.microsoft.gekoppeld || !!d.saml;
  const lijst = groepen ?? d.groepRollen;

  return (
    <div className="pagina kolom" style={{ gap: 16, maxWidth: 1100 }}>
      <div className="paneel">
        <div className="sectie-kop"><h1 className="groei"><Icoon naam="shield-check" maat={20} /> Beveiliging en inloggen</h1></div>
        <p className="klein mute" style={{ marginTop: 0 }}>
          Laat medewerkers inloggen met het account van jullie organisatie (single sign-on), en zet bsmartr in Microsoft Teams. Alle wijzigingen komen in het audit-log.
        </p>
      </div>

      {/* SPEC §24.2 */}
      <div className="paneel kolom" style={{ gap: 10 }}>
        <h2>Microsoft Teams</h2>
        <p className="klein" style={{ margin: 0 }}>Zet bsmartr als app in Teams. Medewerkers zien dan <b>Start</b>, <b>Mijn taken</b> en het <b>Weekrooster</b> in de zijbalk en loggen vanzelf in met hun Microsoft-account.</p>
        {d.teams.beschikbaar ? (
          <>
            <div><a className="knop" href="/api/teams/app.zip" download="bsmartr-teams.zip"><Icoon naam="download" maat={16} /> Teams-app downloaden</a></div>
            <ol className="klein" style={{ margin: 0, paddingLeft: 18 }}>
              <li>Teams-beheerder: <i>Teams-beheercentrum › Teams-apps › Apps beheren › Uploaden</i> en kies het zip-bestand. Of zelf in Teams: <i>Apps › Apps beheren › Een app uploaden</i>.</li>
              <li>Zet de app eventueel vast voor iedereen via een <i>app-installatiebeleid</i>.</li>
              <li>De eerste keer vraagt Microsoft om toestemming voor bsmartr; een Entra-beheerder kan die voor de hele organisatie geven (Instellingen › Outlook en processen).</li>
            </ol>
          </>
        ) : (
          <p className="klein mute" style={{ margin: 0 }}>Inloggen met Microsoft is niet ingesteld op deze server.</p>
        )}
      </div>

      {/* SPEC §25.1 */}
      <div className="paneel kolom" style={{ gap: 10 }}>
        <h2>Domeinen</h2>
        <p className="klein mute" style={{ margin: 0 }}>Bewijs dat een e-maildomein van jullie is. Daarna kun je inloggen met de werkmail, SAML en SSO verplicht voor dat domein gebruiken.</p>
        {d.domeinen.length > 0 && (
          <table className="tabel">
            <thead><tr><th>Domein</th><th>Status</th><th>DNS-record (TXT)</th><th /></tr></thead>
            <tbody>
              {d.domeinen.map((x) => (
                <tr key={x.id}>
                  <td className="vet">{x.domein}</td>
                  <td>{x.geverifieerd ? <Badge toon="groen">geverifieerd</Badge> : <Badge toon="grijs">nog niet</Badge>}</td>
                  <td>
                    {!x.geverifieerd && (
                      <div className="kolom" style={{ gap: 4 }}>
                        <Kopie label="Naam" waarde={x.record.naam} />
                        <Kopie label="Waarde" waarde={x.record.waarde} />
                      </div>
                    )}
                  </td>
                  <td className="nowrap">
                    {!x.geverifieerd && <button className="knop klein" disabled={bezig} onClick={() => void doe(() => api.post(`/api/sso/domeinen/${x.id}/controleer`, {}), `${x.domein} is geverifieerd`)}>Controleren</button>}{' '}
                    <VerwijderKnop klein onBevestig={() => void doe(() => api.del(`/api/sso/domeinen/${x.id}`), 'Domein verwijderd')} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        <div className="rij" style={{ gap: 8 }}>
          <input value={domein} onChange={(e) => setDomein(e.target.value)} placeholder="bedrijf.nl" style={{ maxWidth: 260 }} onKeyDown={(e) => e.key === 'Enter' && domein.trim() && void doe(() => api.post('/api/sso/domeinen', { domein }), 'Domein toegevoegd').then((ok) => ok && setDomein(''))} />
          <button className="knop" disabled={bezig || !domein.trim()} onClick={() => void doe(() => api.post('/api/sso/domeinen', { domein }), 'Domein toegevoegd').then((ok) => ok && setDomein(''))}><Icoon naam="plus" maat={16} /> Domein</button>
        </div>
      </div>

      {/* SPEC §25.2 */}
      <div className="paneel kolom" style={{ gap: 10 }}>
        <h2>SAML (Okta, OneLogin, ADFS, Google Workspace …)</h2>
        <p className="klein mute" style={{ margin: 0 }}>Werken jullie met Microsoft 365? Dan is SAML niet nodig: inloggen met Microsoft is al single sign-on. SAML is voor andere identiteitsproviders.</p>
        <div className="kolom" style={{ gap: 4 }}>
          <div className="klein vet">Vul dit in bij je identiteitsprovider</div>
          <Kopie label="Entity ID" waarde={d.sp.entityId} />
          <Kopie label="ACS-URL" waarde={d.sp.acsUrl} />
          <Kopie label="SP-metadata" waarde={d.sp.metadataUrl} />
          <Kopie label="NameID" waarde="urn:oasis:names:tc:SAML:1.1:nameid-format:emailAddress" />
        </div>
        {d.saml && !saml && (
          <div className="kolom" style={{ gap: 6 }}>
            <div className="rij wrap" style={{ gap: 8 }}>
              <Badge toon={d.saml.getest ? 'groen' : 'grijs'}>{d.saml.getest ? `getest ${new Date(d.saml.getest).toLocaleString('nl-NL', { dateStyle: 'short', timeStyle: 'short' })}` : 'nog niet getest'}</Badge>
              <span className="klein">{d.saml.idpEntityId}</span>
              <span className="klein mute">{d.saml.certificaten} certificaat{d.saml.certificaten === 1 ? '' : 'en'}</span>
            </div>
            <div className="rij wrap" style={{ gap: 8 }}>
              <a className="knop klein" href={`${d.sp.entityId}?test=1`}><Icoon naam="play" maat={14} /> Test-login</a>
              <button className="knop klein" onClick={() => setSaml({ metadataUrl: d.saml?.metadataUrl ?? '', metadataXml: '', naamAttribuut: d.saml?.naamAttribuut ?? '', groepAttribuut: d.saml?.groepAttribuut ?? '' })}><Icoon naam="pencil" maat={14} /> Wijzigen</button>
              <VerwijderKnop klein label="SAML verwijderen" onBevestig={() => void doe(() => api.del('/api/sso/saml'), 'SAML verwijderd')} />
            </div>
            <span className="klein mute">De test-login logt je in via SAML. Je adres moet een geverifieerd domein hebben.</span>
          </div>
        )}
        {!d.saml && !saml && <div><button className="knop" onClick={() => setSaml({ metadataUrl: '', metadataXml: '', naamAttribuut: '', groepAttribuut: '' })}><Icoon naam="plus" maat={16} /> SAML instellen</button></div>}
        {saml && (
          <div className="grid2">
            <Veld label="Metadata-URL van je identiteitsprovider" className="span2"><input value={saml.metadataUrl} onChange={(e) => setSaml({ ...saml, metadataUrl: e.target.value })} placeholder="https://…/metadata" /></Veld>
            <Veld label="of plak de metadata-XML" className="span2"><textarea rows={4} value={saml.metadataXml} onChange={(e) => setSaml({ ...saml, metadataXml: e.target.value })} placeholder="<EntityDescriptor …>" /></Veld>
            <Veld label="Attribuut voor de naam (optioneel)" hint="Standaard displayName of name"><input value={saml.naamAttribuut} onChange={(e) => setSaml({ ...saml, naamAttribuut: e.target.value })} /></Veld>
            <Veld label="Attribuut voor groepen (optioneel)" hint="Standaard groups"><input value={saml.groepAttribuut} onChange={(e) => setSaml({ ...saml, groepAttribuut: e.target.value })} /></Veld>
            <div className="rij span2" style={{ gap: 8 }}>
              <button className="knop" onClick={() => setSaml(null)}>Annuleren</button>
              <button className="knop primair" disabled={bezig || (!saml.metadataUrl.trim() && !saml.metadataXml.trim())} onClick={() => void doe(() => api.put('/api/sso/saml', { metadataUrl: saml.metadataUrl.trim() || null, metadataXml: saml.metadataXml.trim() || null, naamAttribuut: saml.naamAttribuut.trim() || null, groepAttribuut: saml.groepAttribuut.trim() || null }), 'SAML opgeslagen; doe nu een test-login').then((ok) => ok && setSaml(null))}>Opslaan</button>
            </div>
          </div>
        )}
      </div>

      {/* SPEC §25.4 */}
      <div className="paneel kolom" style={{ gap: 10 }}>
        <h2>SSO verplicht</h2>
        <p className="klein" style={{ margin: 0 }}>
          Medewerkers met een geverifieerd domein loggen dan alleen nog in via het bedrijfsaccount ({[d.microsoft.gekoppeld && 'Microsoft-werkaccount', d.saml && 'SAML'].filter(Boolean).join(' of ') || 'nog niet ingesteld'}). Google, privéaccounts en andere methoden werken dan niet meer voor die adressen. Externe adressen houden hun gewone inlogmethode.
        </p>
        <label className="check">
          <input
            type="checkbox"
            checked={d.verplicht}
            disabled={bezig || (!d.verplicht && (!geverifieerd || !bedrijfsMethode || !d.ikViaBedrijf))}
            onChange={(e) => void doe(() => api.put('/api/sso', { verplicht: e.target.checked }), e.target.checked ? 'SSO is nu verplicht' : 'SSO is niet meer verplicht')}
          />{' '}
          SSO verplicht voor {geverifieerd ? d.domeinen.filter((x) => x.geverifieerd).map((x) => x.domein).join(', ') : 'geverifieerde domeinen'}
        </label>
        {!d.verplicht && (!geverifieerd || !bedrijfsMethode || !d.ikViaBedrijf) && (
          <p className="klein mute" style={{ margin: 0 }}>
            Nodig: {!geverifieerd && 'een geverifieerd domein; '}
            {!bedrijfsMethode && 'een bedrijfsmethode (log in met je Microsoft-werkaccount of stel SAML in); '}
            {bedrijfsMethode && !d.ikViaBedrijf && 'zelf ingelogd zijn via het bedrijfsaccount (log uit en weer in met Microsoft of je werkmail), zodat je jezelf niet buitensluit.'}
          </p>
        )}
      </div>

      {/* SPEC §25.5 */}
      <div className="paneel kolom" style={{ gap: 10 }}>
        <h2>Rollen uit groepen</h2>
        <p className="klein mute" style={{ margin: 0 }}>
          Zit iemand in een van deze groepen, dan krijgt hij bij het inloggen via het bedrijfsaccount die rol (de hoogste geldt) en wordt hij zo nodig aangemaakt. Microsoft: gebruik de <b>object-id</b> van de groep en zet in de app-registratie de groepsclaim aan. SAML: de groepsnaam zoals je identiteitsprovider die stuurt.
        </p>
        {lijst.length > 0 && (
          <table className="tabel">
            <thead><tr><th>Groep</th><th>Rol</th><th /></tr></thead>
            <tbody>
              {lijst.map((g, i) => (
                <tr key={i}>
                  <td><input value={g.groep} onChange={(e) => setGroepen(lijst.map((x, j) => (j === i ? { ...x, groep: e.target.value } : x)))} placeholder="groeps-id of naam" /></td>
                  <td>
                    <select value={g.rol} onChange={(e) => setGroepen(lijst.map((x, j) => (j === i ? { ...x, rol: e.target.value as Rol } : x)))}>
                      {ROLLEN.map((r) => <option key={r} value={r}>{ROL_LABEL[r]}</option>)}
                    </select>
                  </td>
                  <td><button className="icoon-knop" title="Weghalen" onClick={() => setGroepen(lijst.filter((_, j) => j !== i))}><Icoon naam="x" maat={14} /></button></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        <div className="rij" style={{ gap: 8 }}>
          <button className="knop" onClick={() => setGroepen([...lijst, { groep: '', rol: 'ENGINEER' }])}><Icoon naam="plus" maat={16} /> Groep</button>
          {groepen && (
            <>
              <button className="knop" onClick={() => setGroepen(null)}>Annuleren</button>
              <button className="knop primair" disabled={bezig || groepen.some((g) => !g.groep.trim())} onClick={() => void doe(() => api.put('/api/sso', { groepRollen: groepen.map((g) => ({ ...g, groep: g.groep.trim() })) }), 'Groepen opgeslagen').then((ok) => ok && setGroepen(null))}>Opslaan</button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
